import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';

import css from './HeroTrustStrip.module.css';

// Filled, monolithic 24px icons with soft cut-outs (§8.7), one colour (--delowoAccentDeep via CSS, D28);
// decorative (aria-hidden): the label carries the meaning.
const IconProfiles = () => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fillRule="evenodd"
      d="M11.1 2.68a2.4 2.4 0 0 1 1.8 0l6 2.4A1.8 1.8 0 0 1 20 6.75V11c0 4.6-3.05 8.6-7.4 10.4a1.6 1.6 0 0 1-1.2 0C7.05 19.6 4 15.6 4 11V6.75a1.8 1.8 0 0 1 1.1-1.67l6-2.4zM10.6 15.7l-3.4-3.4 1.4-1.4 2 2 4.8-4.8 1.4 1.4-6.2 6.2z"
    />
  </svg>
);

const IconMessages = () => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M6 3h7a3 3 0 0 1 3 3v1.5h-5a4.5 4.5 0 0 0-4.5 4.5h-.2l-1.8 1.9A.9.9 0 0 1 3 13.3V6a3 3 0 0 1 3-3z" />
    <path
      fillRule="evenodd"
      d="M11 9h7a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3v2.1a.9.9 0 0 1-1.5.7L13.1 18H11a3 3 0 0 1-3-3v-3a3 3 0 0 1 3-3zm.7 2.9a.7.7 0 0 0 0 1.4h5.6a.7.7 0 0 0 0-1.4zm0 2.5a.7.7 0 0 0 0 1.4h3.1a.7.7 0 0 0 0-1.4z"
    />
  </svg>
);

const IconReviews = () => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      className={css.iconRounded}
      d="M12 3.6l2.5 5.1 5.6.8-4.05 3.95.95 5.6L12 16.4l-5 2.65.95-5.6L3.9 9.5l5.6-.8z"
    />
  </svg>
);

const IconSupport = () => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fillRule="evenodd"
      d="M6 3.5h12a3 3 0 0 1 3 3V14a3 3 0 0 1-3 3h-6.1l-4.4 3.5A.9.9 0 0 1 6 19.8V17a3 3 0 0 1-3-3V6.5a3 3 0 0 1 3-3zm2.5 6a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5zm3.5 0a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5zm3.5 0a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5z"
    />
  </svg>
);

// Each item links to the matching Trust Center item on /p/about (ids in AboutPage.js).
const TRUST_ITEMS = [
  { key: 'profiles', anchor: 'trust-profiles', Icon: IconProfiles },
  { key: 'messages', anchor: 'trust-messages', Icon: IconMessages },
  { key: 'reviews', anchor: 'trust-reviews', Icon: IconReviews },
  { key: 'support', anchor: 'trust-support', Icon: IconSupport },
];

/**
 * B4 / D15: trust strip. SectionHero renders it after the chips in the text column (below 1024px:
 * one column below 768px, 2×2 from 768px) and, with `vertical`, under the feed or the photo in the
 * right column (≥1024px, 2×2 with top-aligned rows since D40); CSS
 * shows one copy per breakpoint (display: none also removes the hidden copy from the a11y tree).
 * The horizontal variant is one left-aligned row when the four items fit its own width
 * (container query), otherwise 2×2.
 * Four claims confirmed by the owner. No payment wording (D24: the platform takes no payments);
 * the reviews claim is a temporary exception until reviews are live (D24).
 */
const HeroTrustStrip = props => {
  const { className, vertical = false } = props;
  const intl = useIntl();
  return (
    // <section>, not <div>: the hero's `> div` rules target the search form wrapper.
    <section className={classNames(css.container, className)}>
      <ul
        className={classNames(css.root, { [css.vertical]: vertical })}
        aria-label={intl.formatMessage({ id: 'SectionHero.trust.label' })}
      >
        {TRUST_ITEMS.map(({ key, anchor, Icon }) => (
          <li key={key} className={css.item}>
            <NamedLink name="AboutPage" to={{ hash: `#${anchor}` }} className={css.link}>
              <Icon />
              <span className={css.text}>
                <FormattedMessage id={`SectionHero.trust.${key}`} />
              </span>
            </NamedLink>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default HeroTrustStrip;
